import { Conversation } from '@elevenlabs/client';
import { toast } from 'sonner';
import { AgentConfig, PitchType, Provider, TranscriptEntry, VoiceAgentInterface, VoiceGender } from '../types';

/**
 * Voice agent backing the legacy `VoiceAgentModal` practice-call flow.
 *
 * Only the ElevenLabs Conversational AI path is implemented. An OpenAI Realtime
 * (`@openai/agents`) path previously lived here, but `VoiceAgentModal` always
 * pinned `provider: 'elevenlabs'`, so the OpenAI branch was never reachable and
 * was removed along with the `@openai/agents` dependency.
 */
export class VoiceAgent implements VoiceAgentInterface {
	private apiKey: string;
	private provider: Provider;
	private transcript: TranscriptEntry[] = [];
	private onTranscriptUpdate?: (transcript: TranscriptEntry[]) => void;
	private onNewTranscriptEntry?: (entry: TranscriptEntry) => void;
	private elevenLabsConversation: Conversation | null = null;
	private conversationId: string | null = null;

	constructor(config: AgentConfig, _useRandomization = true) {
		this.apiKey = config.apiKey;
		this.provider = config.provider || 'elevenlabs';
		this.onTranscriptUpdate = config.onTranscriptUpdate;
		this.onNewTranscriptEntry = config.onNewTranscriptEntry;
	}

	async connect(pitchType: PitchType, customPrompt?: string, voiceGender?: VoiceGender): Promise<void> {
		if (this.provider === 'elevenlabs') {
			await this.connectElevenLabs(pitchType, customPrompt, voiceGender);
		} else {
			throw new Error(`Unsupported provider: ${this.provider}`);
		}
	}

	private async connectElevenLabs(
		pitchType: PitchType,
		customPrompt?: string,
		_voiceGender?: VoiceGender
	): Promise<void> {
		// Detect connection type based on apiKey format:
		// - Signed URLs start with "wss://" -> use WebSocket
		// - Conversation tokens don't -> use WebRTC
		const isSignedUrl = this.apiKey.startsWith('wss://');

		const options = {
			// Dynamic auth: signedUrl for WebSocket, conversationToken for WebRTC
			...(isSignedUrl ? { signedUrl: this.apiKey } : { conversationToken: this.apiKey }),
			overrides: {
				agent: {
					prompt: {
						prompt: customPrompt || `You are a sales prospect. Act as a potential customer for a ${pitchType} call.`,
					},
					// Blank out the first message to let the sales rep initiate the conversation
					firstMessage: '',
				},
			},
			onConnect: ({ conversationId }: { conversationId: string }) => {
				this.conversationId = conversationId;
				console.log(`Connected to ElevenLabs via ${isSignedUrl ? 'WebSocket' : 'WebRTC'}`);
				toast.success('Connected to ElevenLabs');
			},
			onMessage: ({ message, source }: { message: string; source: string }) => {
				const transcriptEntry: TranscriptEntry = {
					speaker: source === 'user' ? 'Sales Rep' : 'Prospect',
					message: message || '',
					timestamp: new Date(),
				};

				this.transcript.push(transcriptEntry);

				if (this.onTranscriptUpdate) {
					this.onTranscriptUpdate([...this.transcript]);
				}

				if (this.onNewTranscriptEntry) {
					this.onNewTranscriptEntry(transcriptEntry);
				}
			},
		};

		// Type assertion needed due to discriminated union in SDK types
		this.elevenLabsConversation = await Conversation.startSession(
			options as Parameters<typeof Conversation.startSession>[0]
		);
	}

	getTranscript(): TranscriptEntry[] {
		return [...this.transcript];
	}

	getFormattedTranscript(): string {
		return this.transcript
			.map(entry => {
				const time = entry.timestamp.toLocaleTimeString();
				return `[${time}] ${entry.speaker}: ${entry.message}`;
			})
			.join('\n');
	}

	clearTranscript(): void {
		this.transcript = [];
		if (this.onTranscriptUpdate) {
			this.onTranscriptUpdate([]);
		}
	}

	getConversationId(): string | null {
		return this.conversationId;
	}

	disconnect(): void {
		if (this.elevenLabsConversation) {
			// Clean up ElevenLabs conversation
			this.elevenLabsConversation.endSession();
			this.elevenLabsConversation = null;
		}

		// Clear conversationId on disconnect
		this.conversationId = null;
	}
}
